import { getData } from "src/utils/axios/serverHelper";
import config from "src/config";
import { ResponseDto } from "src/dtos/common/responseDto";
import {
	TQC3020_R01SalesDto,
	TQC3020_R01SalesLotDto,
	TQC3020_R01ProductBaseDto,
	TQC3020_R01ProductLotDto,
	TQC3020_R01ProductResultDto,
	TQC3020_R01BomDto,
	TQC3020_R01StockDto,
	TQC3020_R01SalesFilter,
	TQC3020_R01ProductBaseFilter,


} from "./Dto_TQC3020_R01";

const baseUrl = `${config.apiUrl}/TQC3020_R01`;

// ========== 매출기준 조회 ==========

/**
 * 매출 헤더/상세 정보 조회
 * @param option - 'search' 또는 'refetch' (페이징 옵션), 선택사항
 * @param params - 조회 필터 파라미터
 * @returns 매출 헤더/상세 정보 목록
 */
export const getTQC3020_R01SalesList = async (
	option?: string,
	params?: Partial<TQC3020_R01SalesFilter>
) => {
	const url = option ? `${baseUrl}/sales/${option}` : `${baseUrl}/sales`;
	
	const requestParams: Record<string, string | number> = {};
	if (params?.startDate) requestParams.startDate = params.startDate;
	if (params?.endDate) requestParams.endDate = params.endDate;
	if (params?.compCdCondition) requestParams.compCdCondition = params.compCdCondition;
	if (params?.customCdCondition) requestParams.customCdCondition = params.customCdCondition;
	if (params?.itemCdCondition) requestParams.itemCdCondition = params.itemCdCondition;
	if (params?.listLength !== undefined) requestParams.listLength = params.listLength;
	if (params?.refetchListLength !== undefined) requestParams.refetchListLength = params.refetchListLength;

	return await getData<ResponseDto<TQC3020_R01SalesDto[]>>(url, { params: requestParams });
};

/**
 * 매출 헤더/상세 정보 전체 개수 조회
 * @param params - 조회 필터 파라미터
 * @returns 전체 개수
 */
export const getTQC3020_R01SalesAllCount = async (
	params?: Partial<TQC3020_R01SalesFilter>
) => {
	const url = `${baseUrl}/sales/allCount`;
	
	const requestParams: Record<string, string | number> = {};
	if (params?.startDate) requestParams.startDate = params.startDate;
	if (params?.endDate) requestParams.endDate = params.endDate;
	if (params?.compCdCondition) requestParams.compCdCondition = params.compCdCondition;
	if (params?.customCdCondition) requestParams.customCdCondition = params.customCdCondition;
	if (params?.itemCdCondition) requestParams.itemCdCondition = params.itemCdCondition;

	return await getData<ResponseDto<number>>(url, { params: requestParams });
};

/**
 * 매출기준 Lot 조회
 * @param salesBillNo - 매출전표번호 (필수)
 * @returns 매출기준 Lot 목록
 */
export const getTQC3020_R01SalesLotList = async (
	salesBillNo: string
) => {
	const url = `${baseUrl}/salesLot/${salesBillNo}`;
	
	return await getData<ResponseDto<TQC3020_R01SalesLotDto[]>>(url);
};

// ========== 생산기준 조회 ==========

/**
 * 생산기준 조회 (LOT 제외)
 * @param option - 'search' 또는 'refetch' (페이징 옵션), 선택사항
 * @param params - 조회 필터 파라미터
 * @returns 생산기준 정보 목록
 */
export const getTQC3020_R01ProductList = async (
	option?: string,
	params?: Partial<TQC3020_R01ProductBaseFilter>
) => {
	const url = option ? `${baseUrl}/productBase/${option}` : `${baseUrl}/productBase`;
	
	const requestParams: Record<string, string | number> = {};
	if (params?.startDate) requestParams.startDate = params.startDate;
	if (params?.endDate) requestParams.endDate = params.endDate;
	if (params?.compCdCondition) requestParams.compCdCondition = params.compCdCondition;
	if (params?.itemCdCondition) requestParams.itemCdCondition = params.itemCdCondition;
	if (params?.workplaceCdCondition) requestParams.workplaceCdCondition = params.workplaceCdCondition;
	if (params?.listLength !== undefined) requestParams.listLength = params.listLength;
	if (params?.refetchListLength !== undefined) requestParams.refetchListLength = params.refetchListLength;

	return await getData<ResponseDto<TQC3020_R01ProductBaseDto[]>>(url, { params: requestParams });
};

/**
 * 생산기준 Lot 조회
 * @param productResultNo - 생산실적번호 (필수)
 * @returns 생산기준 Lot 목록
 */
export const getTQC3020_R01ProductLotList = async (
	productResultNo: string
) => {
	const url = `${baseUrl}/productLot/${productResultNo}`;
	
	return await getData<ResponseDto<TQC3020_R01ProductLotDto[]>>(url);
};

/**
 * Lot 생산 정보 조회 (Lot 번호 기준)
 * @param lotNo - Lot 번호 (필수)
 * @returns 생산실적 리스트
 */
export const getTQC3020_R01ProductionHistory = async (
	lotNo: string
) => {
	const url = `${baseUrl}/productResult/${lotNo}`;
	
	return await getData<ResponseDto<TQC3020_R01ProductResultDto[]>>(url);
};


// ========== 자재·재공품 투입(소비) 내역 조회 ==========

/**
 * 자재·재공품 투입(소비) 내역 Lot 조회
 * @param productResultNo - 생산실적번호 (필수)
 * @returns 자재·재공품 투입(소비) 내역 목록
 */
export const getTQC3020_R01MaterialConsumption = async (
	productResultNo: string
) => {
	const url = `${baseUrl}/bom/${productResultNo}`;
	
	return await getData<ResponseDto<TQC3020_R01BomDto[]>>(url);
};

// ========== 자재·재공품 Lot 입고 근거 조회 ==========

/**
 * 자재·재공품 Lot 입고 근거 조회
 * @param lotNo - Lot 번호 (필수)
 * @returns 자재·재공품 Lot 입고 근거 목록
 */
export const getTQC3020_R01MaterialInbound = async (
	lotNo: string
) => {
	const url = `${baseUrl}/stock/${lotNo}/inboundHistory`;
	
	return await getData<ResponseDto<TQC3020_R01StockDto[]>>(url);
};


